import React, { useState, useEffect, useRef } from 'react';
import { CLIENTS } from '../mock/mock';

// Ensure enough visible items for smooth coverflow — repeat until at least 18
const buildTrack = () => {
  const arr = [];
  while (arr.length < 24) arr.push(...CLIENTS);
  return arr;
};

const ClientBrand = ({ client, magnified, showName = true }) => {
  return (
    <div
      className={`rcd-brand-card ${magnified ? 'rcd-brand-card--focus' : ''}`}
      aria-current={magnified}
    >
      <div className="rcd-brand-logo">
        {client.type === 'image' ? (
          <img
            src={client.logo}
            alt={client.name}
            className={`max-h-full max-w-full object-contain transition-all duration-500 ${
              magnified ? 'grayscale-0 opacity-100' : 'grayscale opacity-70'
            }`}
            loading="lazy"
          />
        ) : (
          <span
            className={`font-serif tracking-[0.18em] text-center leading-none transition-colors duration-500 ${
              magnified ? 'text-[#a67c3d] text-2xl md:text-3xl' : 'text-[#3a2f27] text-lg md:text-xl'
            }`}
          >
            {client.logo}
          </span>
        )}
      </div>
      {showName && (
        <p
          className={`rcd-brand-name transition-all duration-500 ${
            magnified
              ? 'text-[#1a1614] text-sm md:text-base tracking-[0.24em] font-medium'
              : 'text-[#6b5847] text-[10px] md:text-xs tracking-[0.2em]'
          }`}
        >
          {client.name}
        </p>
      )}
    </div>
  );
};

const ClientsMarquee = ({ background = '#f2ebde', speed = 60, showName = true }) => {
  const track = buildTrack();
  const doubled = [...track, ...track];

  const wrapRef = useRef(null);
  const [centerIdx, setCenterIdx] = useState(-1);

  // Continuously detect which card is closest to viewport center for the magnification effect
  useEffect(() => {
    let raf;
    const measure = () => {
      if (!wrapRef.current) return;
      const wrapRect = wrapRef.current.getBoundingClientRect();
      const wrapCenter = wrapRect.left + wrapRect.width / 2;
      const cards = wrapRef.current.querySelectorAll('.rcd-brand-card');
      let best = -1;
      let bestDist = Infinity;
      cards.forEach((c, i) => {
        const r = c.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const d = Math.abs(cx - wrapCenter);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      setCenterIdx(best);
      raf = requestAnimationFrame(measure);
    };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section className="py-20 md:py-24 overflow-hidden relative" style={{ background }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-10 flex items-end justify-between">
        <div>
          <p className="text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-2">Trusted by</p>
          <h2 className="font-serif text-2xl md:text-3xl text-[#1a1614]">Hospitality leaders, worldwide</h2>
        </div>
        <p className="hidden md:block text-xs tracking-[0.24em] uppercase text-[#8a7f6f]">
          Trusted partners
        </p>
      </div>

      <div ref={wrapRef} className="rcd-brand-marquee-wrap relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-40 z-10" style={{ background: `linear-gradient(90deg, ${background} 0%, rgba(255,255,255,0) 100%)` }} />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-40 z-10" style={{ background: `linear-gradient(270deg, ${background} 0%, rgba(255,255,255,0) 100%)` }} />

        <div
          className="rcd-brand-marquee-track"
          style={{ animationDuration: `${speed}s` }}
        >
          {doubled.map((c, i) => (
            <ClientBrand
              key={`${c.name}-${i}`}
              client={c}
              magnified={i === centerIdx}
              showName={showName}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClientsMarquee;
